<template>
  <div>
    <!-- Status incidents banner -->
    <div
      v-if="shouldShowStatusBanner"
      @click="openStatusPage"
      class="relative"
    >
      <!-- Dismiss button -->
      <button
        @click.stop="dismissBanner"
        class="absolute top-1/2 -translate-y-1/2 right-4 z-10 text-white hover:text-gray-200 transition-colors"
        aria-label="Dismiss banner"
      >
        <o-icon icon="x" :size="16" />
      </button>

      <!-- Show ongoing incidents -->
      <div v-if="hasOngoingIncidents" class="space-y-2 bg-red-600 py-3 px-5 pr-10">
        <a
          v-for="incident in statusData.ongoing_incidents"
          :key="incident.id"
          class="flex items-center space-x-2"
          :href="incident.url"
          target="_blank group block"
        >
          <o-icon icon="warning" class="text-white mr-5 inline-block" :size="18" />
          <div>
            <div class="flex items-center space-x-2">
              <p class="text-white font-semibold leading-none text-sm group-hover:underline underline-white">
                We're currently experiencing issues - {{ incident.name }}
              </p>
              <span class="text-white/75 text-sm">
                {{ formatDate(incident.last_update_at) }}
              </span>
            </div>
            <p class="text-white/90 text-sm leading-tight">
              {{ incident.last_update_message }}
            </p>
          </div>
        </a>
      </div>

      <!-- Show in-progress maintenance if no ongoing incidents -->
      <div v-else-if="hasInProgressMaintenance" class="bg-orange-500 py-3 px-5 pr-10">
        <div
          v-for="maintenance in statusData.in_progress_maintenances.slice(0, 2)"
          :key="maintenance.id"
        >
          <p class="text-white/90 text-sm leading-tight">
            We're currently undergoing maintenance - {{ maintenance.name }}
          </p>
        </div>
      </div>

      <!-- Show scheduled maintenance if no other issues -->
      <div v-else-if="hasScheduledMaintenance" class="bg-blue-600 py-3 px-5 pr-10">
        <div
          v-for="maintenance in statusData.scheduled_maintenances.slice(0, 2)"
          :key="maintenance.id"
          class="border-l-2/30 border-white pl-3"
        >
          <p class="text-white/90 text-sm leading-tight">
            Scheduled Maintenance - {{ maintenance.name }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import axios from 'axios'

const statusData = ref<any>(null)
const statusError = ref(false)
const dismissed = ref(false)
let statusInterval: ReturnType<typeof setInterval> | null = null

const hasOngoingIncidents = computed(() => statusData.value?.ongoing_incidents?.length > 0)
const hasInProgressMaintenance = computed(() => statusData.value?.in_progress_maintenances?.length > 0)
const hasScheduledMaintenance = computed(() => statusData.value?.scheduled_maintenances?.length > 0)
const shouldShowStatusBanner = computed(() => {
  return !dismissed.value && (hasOngoingIncidents.value || hasInProgressMaintenance.value || hasScheduledMaintenance.value)
})

const fetchStatusData = async () => {
  try {
    const response = await axios.get('https://status.ovatu.com/api/v1/summary')
    statusData.value = response.data
    statusError.value = false
  } catch (error) {
    console.warn('Failed to fetch status data:', error)
    statusError.value = true
  }
}

const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString)
    const now = new Date()
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))

    if (diffInMinutes < 1) {
      return 'Just now'
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60)
      return `${hours}h ago`
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })
    }
  } catch (error) {
    return ''
  }
}

const openStatusPage = () => {
  if (statusData.value?.page_url) {
    window.open(statusData.value.page_url, '_blank')
  }
}

const dismissBanner = () => {
  dismissed.value = true
}

onMounted(async () => {
  await fetchStatusData()
  statusInterval = setInterval(() => {
    fetchStatusData()
  }, 5 * 60 * 1000)
})

onBeforeUnmount(() => {
  if (statusInterval) {
    clearInterval(statusInterval)
  }
})
</script>
